import { DehydrateOptions, MutationKey, QueryKey } from 'react-query';
import { createWebStoragePersistor } from 'react-query/createWebStoragePersistor-experimental';
import {
  PersistedClient,
  PersistQueryClientOptions,
} from 'react-query/types/persistQueryClient-experimental';
import { persistQueryClient } from './persistQueryClient';
import { matchMutation, matchQuery } from './utils';

type ConfigPersistQueryClient = Omit<PersistQueryClientOptions, 'persistor'> & {
  storage?: Storage;
  key?: string;
  throttleTime?: number;
  serialize?: (client: PersistedClient) => string;
  deserialize?: (string: string) => PersistedClient;
  excludePersistQuery?: (QueryKey | undefined)[];
  excludePersistMutation?: (MutationKey | undefined)[];
};

/**
 * Method for creating a new instance of the QueryClient.
 * @param config.maxAge The max-allowed age of the cache. If a persisted cache is found that is older than this time, it will be discarded.
 * @param config.buster A unique string that can be used to forcefully invalidate existing caches if they do not share the same buster string
 * @param config.storage The storage client used for setting an retrieving items from cache.
 * @param config.key The key to use when storing the cache.
 * @param config.throttleTime To avoid spamming, pass a time in ms to throttle saving the cache to disk.
 * @param config.serialize How to serialize the data to storage. default `JSON.stringify`.
 * @param config.deserialize How to deserialize the data from storage. default: `JSON.parse`.
 * @param config.excludePersistQuery Array keys of query for exlude to the dehydrate function.
 * @param config.excludePersistMutation Array keys of mutation for exlude to the dehydrate function.
 * @return Instance QueryClient
 */
export const initializePersistQueryClient = ({
  queryClient,
  maxAge,
  buster,
  key,
  throttleTime,
  excludePersistMutation = [],
  excludePersistQuery = [],
  storage = window.localStorage,
  dehydrateOptions,
  hydrateOptions,
}: ConfigPersistQueryClient) => {
  const localStoragePersistor = createWebStoragePersistor({
    storage,
    key,
    throttleTime,
  });

  const customDehydrateOptions: DehydrateOptions = {
    shouldDehydrateMutation: (mutation) =>
      !excludePersistMutation.some((mutationKey) =>
        matchMutation({ mutationKey }, mutation),
      ),
    shouldDehydrateQuery: (query) =>
      !excludePersistQuery.some((queryKey) => matchQuery({ queryKey }, query)),
  };

  persistQueryClient({
    queryClient,
    persistor: localStoragePersistor,
    maxAge,
    buster,
    hydrateOptions: hydrateOptions,
    dehydrateOptions: dehydrateOptions ? dehydrateOptions : customDehydrateOptions,
  });
};
